import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth/AuthContext';
import { ActivityLog } from '@/types/supabase';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, AlertCircle, FileText, Users, Building2, ChartBar, Database, LogIn, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ActivityLogsFullView } from './ActivityLogsFullView';

export function ActivityLogsList() {
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentOrganization } = useAuth();
  const [showFullView, setShowFullView] = useState(false);

  useEffect(() => {
    if (!currentOrganization) return;

    async function fetchActivityLogs() {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch only the latest 3 activity logs
        const { data, error } = await supabase
          .from('activity_logs')
          .select('*')
          .eq('organization_id', currentOrganization.organization.id)
          .order('created_at', { ascending: false })
          .limit(3);

        if (error) throw error;

        // For logs with user_id, fetch user emails separately
        const logsWithUserInfo = await Promise.all(
          data.map(async (log) => {
            if (log.user_id) {
              // Fetch user email information
              const { data: userData, error: userError } = await supabase
                .from('user_organizations')
                .select('user_id')
                .eq('user_id', log.user_id)
                .single();
              
              if (!userError && userData) {
                return {
                  ...log,
                  user: {
                    id: log.user_id,
                    email: log.user_id.split('-')[0] + '@example.com' // Fallback for display purposes
                  }
                };
              }
            }
            
            return {
              ...log,
              user: undefined
            };
          })
        );

        setActivityLogs(logsWithUserInfo);
      } catch (err: any) {
        console.error('Error fetching activity logs:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }

    fetchActivityLogs();
  }, [currentOrganization]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" />
        <p>Failed to load activity logs: {error}</p>
      </div>
    );
  }

  if (activityLogs.length === 0) {
    return <p className="text-sm text-muted-foreground">No recent activity found.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {activityLogs.map((log) => (
          <ActivityLogItem key={log.id} log={log} />
        ))}
      </div>
      
      <div className="flex justify-center mt-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowFullView(true)}
        >
          View All Activity
        </Button>
      </div>

      <Sheet open={showFullView} onOpenChange={setShowFullView}>
        <SheetContent className="w-full sm:max-w-[80%] md:max-w-[70%] lg:max-w-[65%] overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Activity Logs</SheetTitle>
            <SheetDescription>
              View and filter all activity in your organization
            </SheetDescription>
          </SheetHeader>
          <ActivityLogsFullView onClose={() => setShowFullView(false)} />
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ActivityLogItem({ log }: { log: ActivityLog }) {
  // Get the appropriate icon based on resource type
  function getActivityIcon() {
    switch (log.resource_type) {
      case 'system':
        return <LogIn className="h-4 w-4" />;
      case 'grievance':
        return <Flag className="h-4 w-4" />;
      case 'user_organizations':
      case 'user':
        return <Users className="h-4 w-4" />;
      case 'department':
        return <Building2 className="h-4 w-4" />;
      case 'analytics':
        return <ChartBar className="h-4 w-4" />;
      case 'document':
        return <FileText className="h-4 w-4" />;
      default:
        return <Database className="h-4 w-4" />;
    }
  }

  // Get background color based on resource type
  function getActivityTypeStyles() {
    switch (log.resource_type) {
      case 'system':
        return 'bg-blue-100 text-blue-700';
      case 'grievance':
        return 'bg-red-100 text-red-700';
      case 'user_organizations':
      case 'user':
        return 'bg-green-100 text-green-700';
      case 'department':
        return 'bg-purple-100 text-purple-700';
      case 'analytics':
        return 'bg-amber-100 text-amber-700';
      case 'document':
      case 'organization':
      case 'project':
        return 'bg-indigo-100 text-indigo-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  // Format the activity description based on the action and resource type
  function getActivityDescription() {
    // For displaying the user who performed the action
    const actor = log.user?.email?.split('@')[0] || 'A user';
    
    const action = log.action.toLowerCase();
    const resourceType = log.resource_type.replace('_', ' ');
    
    let description = `${actor} ${action}d a ${resourceType}`;
    
    // Add more specific details based on the resource type and action
    if (log.details) {
      if (log.resource_type === 'department' && log.details.name) {
        description += `: ${log.details.name}`;
      } else if (log.resource_type === 'project' && log.details.name) {
        description += `: ${log.details.name}`;
      } else if (log.resource_type === 'document' && log.details.title) {
        description += `: ${log.details.title}`;
      } else if (log.resource_type === 'grievance' && log.details.title) {
        description += `: ${log.details.title}`;
      }
    }
    
    return description;
  }

  // Format the time relative to now (e.g., "5 minutes ago")
  function getRelativeTime() {
    return formatDistanceToNow(new Date(log.created_at), { addSuffix: true });
  }

  return (
    <div className="flex items-start gap-3 rounded-lg border p-3 text-sm">
      <div className={`rounded-full ${getActivityTypeStyles()} p-2`}>
        {getActivityIcon()}
      </div>
      <div className="flex-1">
        <p className="font-medium">{getActivityDescription()}</p>
        <p className="text-xs text-muted-foreground">
          {log.action} {log.resource_type}
        </p>
      </div>
      <div className="text-xs text-muted-foreground whitespace-nowrap">
        {getRelativeTime()}
      </div>
    </div>
  );
}
